import { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import type { EntityRecord } from "@wedding/schemas";
import { Badge, EmptyState } from "@wedding/ui";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Inbox,
  Link2,
  Pencil,
  QrCode,
  Trash2,
} from "lucide-react";
import type { ModuleConfig } from "../config";

const helper = createColumnHelper<EntityRecord>();
function display(value: unknown, key: string) {
  if (value === true) return "是";
  if (value === false) return "否";
  if (value == null || value === "") return "—";
  if (key.toLowerCase().includes("cents"))
    return new Intl.NumberFormat("zh-CN", {
      style: "currency",
      currency: "CNY",
    }).format(Number(value) / 100);
  return Array.isArray(value) ? value.join("、") : String(value);
}
function tone(value: unknown) {
  const s = String(value);
  if (["已完成", "出席", "已签", "低", "已执行"].includes(s)) return "success";
  if (["高", "延期", "不出席", "遗失"].includes(s)) return "danger";
  if (["进行中", "待确认", "中", "待评估"].includes(s)) return "warning";
  return "neutral";
}

function primaryFieldKey(config: ModuleConfig) {
  const preferred = [
    "title",
    "vendorName",
    "projectName",
    "name",
    "item",
    "risk",
    "idea",
    "segment",
    "shotName",
    "person",
    "role",
  ];
  return (
    preferred.find((key) => config.fields.some((field) => field.key === key)) ??
    config.fields[0]?.key ??
    "title"
  );
}

export function EntityTable({
  config,
  items,
  selected,
  relationLabels,
  emptyTitle,
  emptyDetail,
  onSelected,
  onView,
  onEdit,
  onDelete,
  onInvite,
  onQrCode,
  onCreate,
  onImport,
  onLibrary,
}: {
  config: ModuleConfig;
  items: EntityRecord[];
  selected: Set<string>;
  relationLabels: Record<string, Record<string, string>>;
  emptyTitle?: string | undefined;
  emptyDetail?: string | undefined;
  onSelected(s: Set<string>): void;
  onView?(x: EntityRecord): void;
  onEdit(x: EntityRecord): void;
  onDelete(x: EntityRecord): void;
  onInvite?(x: EntityRecord): void;
  onQrCode?(x: EntityRecord): void;
  onCreate?(): void;
  onImport?(): void;
  onLibrary?(): void;
}) {
  const primaryKey = primaryFieldKey(config);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = useMemo(
    () => [
      helper.display({
        id: "select",
        enableSorting: false,
        header: () => (
          <input
            className="checkbox"
            type="checkbox"
            checked={items.length > 0 && items.every((x) => selected.has(x.id))}
            onChange={(e) =>
              onSelected(
                e.target.checked ? new Set(items.map((x) => x.id)) : new Set(),
              )
            }
            aria-label="选择当前列表全部记录"
          />
        ),
        cell: ({ row }) => (
          <input
            className="checkbox"
            type="checkbox"
            checked={selected.has(row.original.id)}
            onClick={(event) => event.stopPropagation()}
            onChange={(e) => {
              const n = new Set(selected);
              if (e.target.checked) n.add(row.original.id);
              else n.delete(row.original.id);
              onSelected(n);
            }}
            aria-label={`选择 ${display(row.original.data[primaryKey], "")}`}
          />
        ),
      }),
      ...config.fields.map((f) =>
        helper.accessor((row) => row.data[f.key], {
          id: f.key,
          header: f.label,
          sortUndefined: "last",
          sortingFn: (left, right) => {
            const normalize = (record: EntityRecord) => {
              const raw = record.data[f.key];
              if (f.type === "relation")
                return relationLabels[f.key]?.[String(raw)] ?? "";
              if (f.optionLabels?.[String(raw)])
                return f.optionLabels[String(raw)]!;
              if (f.type === "number" || f.type === "currency")
                return Number(raw ?? 0);
              if (typeof raw === "boolean") return raw ? 1 : 0;
              return String(raw ?? "");
            };
            const leftValue = normalize(left.original);
            const rightValue = normalize(right.original);
            if (typeof leftValue === "number" && typeof rightValue === "number")
              return leftValue - rightValue;
            return String(leftValue).localeCompare(
              String(rightValue),
              "zh-CN",
              {
                numeric: true,
              },
            );
          },
          cell: (ctx) =>
            [
              "status",
              "priority",
              "riskLevel",
              "attendance",
              "decisionStatus",
              "preparationStatus",
              "contractStatus",
            ].includes(f.key) ? (
              <Badge tone={tone(ctx.getValue())}>
                {display(ctx.getValue(), f.key)}
              </Badge>
            ) : (
              <span
                className={
                  f.key.toLowerCase().includes("cents") ? "number" : ""
                }
              >
                {f.type === "relation"
                  ? (relationLabels[f.key]?.[String(ctx.getValue())] ??
                    "未关联")
                  : (f.optionLabels?.[String(ctx.getValue())] ??
                    display(ctx.getValue(), f.key))}
              </span>
            ),
        }),
      ),
      helper.display({
        id: "actions",
        enableSorting: false,
        header: "操作",
        cell: ({ row }) => (
          <div
            className="row-actions"
            onClick={(event) => event.stopPropagation()}
          >
            {config.type === "guests" && onInvite && (
              <button
                type="button"
                onClick={() => onInvite(row.original)}
                aria-label={`复制 ${display(row.original.data[primaryKey], "")} 的邀请链接`}
              >
                <Link2 size={16} />
              </button>
            )}
            {config.type === "guests" && onQrCode && (
              <button
                type="button"
                onClick={() => onQrCode(row.original)}
                aria-label={`生成 ${display(row.original.data[primaryKey], "")} 的邀请二维码`}
              >
                <QrCode size={16} />
              </button>
            )}
            <button
              type="button"
              onClick={() => onEdit(row.original)}
              aria-label={`编辑 ${display(row.original.data[primaryKey], "")}`}
            >
              <Pencil size={16} />
            </button>
            <button
              type="button"
              onClick={() => onDelete(row.original)}
              aria-label={`删除 ${display(row.original.data[primaryKey], "")}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ),
      }),
    ],
    [
      config,
      items,
      onDelete,
      onEdit,
      onInvite,
      onQrCode,
      onSelected,
      relationLabels,
      selected,
    ],
  );
  const table = useReactTable({
    data: items,
    columns,
    state: { columnVisibility, sorting },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 15 } },
  });
  if (!items.length)
    return (
      <div className="guided-empty-state">
        <EmptyState
          icon={<Inbox size={30} />}
          title={emptyTitle ?? `还没有${config.singular}`}
          detail={
            emptyDetail ??
            (onCreate && onImport && onLibrary
              ? `可以新建${config.singular}、从事项库挑选，或导入已有表格。`
              : `可以先新建一条${config.singular}。`)
          }
        />
        {(onCreate || onImport || onLibrary) && (
          <div className="empty-state-actions">
            {onCreate && (
              <button onClick={onCreate}>新建{config.singular}</button>
            )}
            {onLibrary && <button onClick={onLibrary}>从事项库选择</button>}
            {onImport && <button onClick={onImport}>导入 CSV</button>}
          </div>
        )}
      </div>
    );
  return (
    <>
      <div className="column-picker">
        <label className="table-sort-select">
          <span>排序</span>
          <select
            aria-label="选择排序方式"
            value={
              sorting[0]
                ? `${sorting[0].id}:${sorting[0].desc ? "desc" : "asc"}`
                : ""
            }
            onChange={(event) => {
              if (!event.target.value) return setSorting([]);
              const [id, direction] = event.target.value.split(":");
              if (id) setSorting([{ id, desc: direction === "desc" }]);
            }}
          >
            <option value="">默认顺序</option>
            {table
              .getAllLeafColumns()
              .filter((column) => column.getCanSort())
              .flatMap((column) => [
                <option key={`${column.id}:asc`} value={`${column.id}:asc`}>
                  {String(column.columnDef.header)} · 升序
                </option>,
                <option key={`${column.id}:desc`} value={`${column.id}:desc`}>
                  {String(column.columnDef.header)} · 降序
                </option>,
              ])}
          </select>
        </label>
        <details>
          <summary>显示列</summary>
          <div>
            {table
              .getAllLeafColumns()
              .filter((c) => !["select", "actions"].includes(c.id))
              .map((c) => (
                <label key={c.id}>
                  <input
                    type="checkbox"
                    checked={c.getIsVisible()}
                    onChange={c.getToggleVisibilityHandler()}
                  />
                  {String(c.columnDef.header)}
                </label>
              ))}
          </div>
        </details>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            {table.getHeaderGroups().map((g) => (
              <tr key={g.id}>
                {g.headers.map((h) => (
                  <th
                    key={h.id}
                    aria-sort={
                      h.column.getIsSorted() === "asc"
                        ? "ascending"
                        : h.column.getIsSorted() === "desc"
                          ? "descending"
                          : undefined
                    }
                  >
                    {h.isPlaceholder ? null : h.column.getCanSort() ? (
                      <button
                        type="button"
                        className="sortable-header"
                        onClick={h.column.getToggleSortingHandler()}
                        aria-label={`${String(h.column.columnDef.header)}排序`}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getIsSorted() === "asc" ? (
                          <ArrowUp size={13} />
                        ) : h.column.getIsSorted() === "desc" ? (
                          <ArrowDown size={13} />
                        ) : (
                          <ArrowUpDown size={13} />
                        )}
                      </button>
                    ) : (
                      flexRender(h.column.columnDef.header, h.getContext())
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((r) => (
              <tr
                key={r.id}
                tabIndex={0}
                aria-label={`查看 ${display(r.original.data[primaryKey], "")} 的详情`}
                onClick={() => (onView ?? onEdit)(r.original)}
                onKeyDown={(event) => {
                  if (
                    event.target === event.currentTarget &&
                    event.key === "Enter"
                  )
                    (onView ?? onEdit)(r.original);
                }}
              >
                {r.getVisibleCells().map((c) => (
                  <td key={c.id}>
                    {flexRender(c.column.columnDef.cell, c.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mobile-cards">
        {table.getRowModel().rows.map((r) => (
          <article
            key={r.id}
            role="button"
            tabIndex={0}
            aria-label={`查看 ${display(r.original.data[primaryKey], "")} 的详情`}
            onClick={() => (onView ?? onEdit)(r.original)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                (onView ?? onEdit)(r.original);
              }
            }}
          >
            <header>
              <div className="mobile-card-title">
                <input
                  className="checkbox"
                  type="checkbox"
                  checked={selected.has(r.original.id)}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    const next = new Set(selected);
                    if (event.target.checked) next.add(r.original.id);
                    else next.delete(r.original.id);
                    onSelected(next);
                  }}
                  aria-label={`选择 ${display(r.original.data[primaryKey], "")}`}
                />
                <strong>{display(r.original.data[primaryKey], "")}</strong>
              </div>
              <Badge tone={tone(r.original.data.status)}>
                {display(r.original.data.status ?? config.label, "")}
              </Badge>
            </header>
            {config.fields
              .filter((field) => field.key !== primaryKey)
              .map((f) => (
                <p key={f.key}>
                  <span>{f.label}</span>
                  {f.type === "relation"
                    ? (relationLabels[f.key]?.[
                        String(r.original.data[f.key])
                      ] ?? "未关联")
                    : (f.optionLabels?.[String(r.original.data[f.key])] ??
                      display(r.original.data[f.key], f.key))}
                </p>
              ))}
            <footer className="mobile-card-actions">
              {config.type === "guests" && onInvite && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onInvite(r.original);
                  }}
                >
                  <Link2 size={15} /> 复制邀请链接
                </button>
              )}
              {config.type === "guests" && onQrCode && (
                <button
                  type="button"
                  aria-label={`生成 ${display(r.original.data[primaryKey], "")} 的邀请二维码`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onQrCode(r.original);
                  }}
                >
                  <QrCode size={15} /> 邀请二维码
                </button>
              )}
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(r.original);
                }}
              >
                <Pencil size={15} /> 编辑
              </button>
              <button
                type="button"
                className="danger-text"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(r.original);
                }}
              >
                <Trash2 size={15} /> 删除
              </button>
            </footer>
          </article>
        ))}
      </div>
      <footer className="pagination">
        <span>
          第 {table.getState().pagination.pageIndex + 1} /{" "}
          {table.getPageCount()} 页 · 共 {items.length} 条
        </span>
        <div>
          <button
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            上一页
          </button>
          <button
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            下一页
          </button>
        </div>
      </footer>
    </>
  );
}
